import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildTradingServerPayloadFromForm,
  mapPlatformsDictionaryResponseToDto,
  mapTradingServerFieldErrors,
  mapTradingServersResponseToDto,
  validateTradingServerPayload,
} from '../dto/system-trading-servers.dto.js';
import {
  buildEmptyTradingServerForm,
  buildPlatformLabelMap,
  buildTradingServersRowsModel,
} from '../model/system-trading-servers.model.js';

export function useSystemTradingServers() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsTargetId, setDetailsTargetId] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [form, setForm] = useState(buildEmptyTradingServerForm(''));
  const [formErrors, setFormErrors] = useState({});
  const [modalError, setModalError] = useState('');

  const hasPlatforms = platformOptions.length > 0;

  const detailsRow = useMemo(
    () => rows.find((row) => row.id === detailsTargetId) || null,
    [rows, detailsTargetId],
  );

  const platformLabelByValue = useMemo(
    () => buildPlatformLabelMap(platformOptions),
    [platformOptions],
  );

  const loadPlatforms = useCallback(async () => {
    try {
      const response = await adminApi.getPlatformsDictionary(token);
      const nextOptions = mapPlatformsDictionaryResponseToDto(response);
      setPlatformOptions(nextOptions);
      setForm((prev) => ({
        ...prev,
        tradingPlatform: prev.tradingPlatform || nextOptions[0]?.value || '',
      }));
    } catch {
      setPlatformOptions([]);
    }
  }, [token]);

  const loadServers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getTradingServers(token);
      const dto = mapTradingServersResponseToDto(response);
      setRows(buildTradingServersRowsModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setError(requestError.message || 'Failed to load trading servers');
        setRows([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPlatforms();
    loadServers();
  }, [loadPlatforms, loadServers]);

  const closeModals = useCallback(() => {
    if (isSaving) {
      return;
    }

    setForm((prev) => ({ ...prev, managerToken: '' }));
    setDetailsTargetId('');
    setIsCreateOpen(false);
    setEditingTargetId('');
    setDeleteTargetId('');
    setFormErrors({});
    setModalError('');
  }, [isSaving]);

  const openCreate = useCallback(() => {
    setDetailsTargetId('');
    setEditingTargetId('');
    setDeleteTargetId('');
    setIsCreateOpen(true);
    setForm(buildEmptyTradingServerForm(platformOptions[0]?.value || '', platformOptions[0]?.capabilities));
    setFormErrors({});
    setModalError('');
    setError('');
    setSuccess('');
  }, [platformOptions]);

  const openEdit = useCallback((row) => {
    setDetailsTargetId('');
    setDeleteTargetId('');
    setIsCreateOpen(false);
    setEditingTargetId(row.id);
    setForm({
      tradingPlatform: row.tradingPlatform || platformOptions[0]?.value || '',
      capabilities: platformOptions.find(option => option.value === row.tradingPlatform)?.capabilities ?? {},
      name: row.name || '',
      description: row.description || '',
      dsn: row.dsn || '',
      managerToken: '',
      hasManagerToken: row.hasManagerToken === true,
      dealerLogin: row.dealerLogin || '',
      dealerPassword: row.dealerPassword || '',
      dealerDsn: row.dealerDsn || '',
      proxy: row.proxy || '',
      accountTypes: Array.isArray(row.accountTypes) ? row.accountTypes : [],
    });
    setFormErrors({});
    setModalError('');
    setError('');
    setSuccess('');
  }, [platformOptions]);

  const openDetails = useCallback((id) => {
    setDetailsTargetId(id);
  }, []);

  const requestDelete = useCallback((id) => {
    setDeleteTargetId(id);
  }, []);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => {
      if (key !== 'tradingPlatform') return { ...prev, [key]: value };
      const capabilities = platformOptions.find(option => option.value === value)?.capabilities ?? {};
      const allowed = capabilities.allowedAccountTypes ?? ['demo', 'live'];
      return { ...prev, tradingPlatform: value, capabilities, managerToken: '', hasManagerToken: false,
        accountTypes: allowed.length === 1 ? [...allowed] : prev.accountTypes.filter(type => allowed.includes(type)),
        dealerLogin: '', dealerPassword: '', dealerDsn: '' };
    });
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [platformOptions]);

  const toggleAccountType = useCallback((accountType, checked) => {
    const normalizedType = String(accountType || '').trim();
    if (!normalizedType) {
      return;
    }

    setForm((prev) => {
      const current = Array.isArray(prev.accountTypes) ? prev.accountTypes : [];
      const nextSet = new Set(current);
      if (checked) {
        nextSet.add(normalizedType);
      } else {
        nextSet.delete(normalizedType);
      }

      return {
        ...prev,
        accountTypes: Array.from(nextSet),
      };
    });

    setFormErrors((prev) => {
      if (!prev.accountTypes) {
        return prev;
      }
      const next = { ...prev };
      delete next.accountTypes;
      return next;
    });
  }, []);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();

    const payload = buildTradingServerPayloadFromForm(form);
    const nextErrors = validateTradingServerPayload(payload, form.capabilities);
    if (form.capabilities?.credentialType === 'manager_token' && !form.hasManagerToken && !payload.managerToken) nextErrors.managerToken = 'Enter the tenant manager token issued by the trading platform.';

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setModalError('Please fix validation errors.');
      return;
    }

    setIsSaving(true);
    setModalError('');
    setSuccess('');

    try {
      if (editingTargetId) {
        const response = await adminApi.updateTradingServer(token, editingTargetId, payload);
        const dto = mapTradingServersResponseToDto([{ id: editingTargetId, ...(response?.server || payload) }]);
        const updated = buildTradingServersRowsModel(dto)[0];
        setRows((prev) => prev.map((row) => (row.id === editingTargetId ? { ...updated, id: editingTargetId } : row)));
        setEditingTargetId('');
        setFormErrors({});
        setModalError('');
        setSuccess('Trading server updated.');
      } else {
        const response = await adminApi.createTradingServer(token, payload);
        const dto = mapTradingServersResponseToDto([response?.server || payload]);
        const created = buildTradingServersRowsModel(dto)[0];
        setRows((prev) => [...prev, created]);
        setIsCreateOpen(false);
        setFormErrors({});
        setModalError('');
        setSuccess('Trading server created.');
      }
    } catch (requestError) {
      setFormErrors(mapTradingServerFieldErrors(requestError));
      setModalError(requestError.message || 'Failed to save trading server');
    } finally {
      setForm((prev) => ({ ...prev, managerToken: '' }));
      setIsSaving(false);
    }
  }, [editingTargetId, form, token]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId) {
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.deleteTradingServer(token, deleteTargetId);
      setRows((prev) => prev.filter((row) => row.id !== deleteTargetId));
      setDeleteTargetId('');
      setSuccess('Trading server deleted.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to delete trading server');
    } finally {
      setForm((prev) => ({ ...prev, managerToken: '' }));
      setIsSaving(false);
    }
  }, [deleteTargetId, token]);

  return {
    rows,
    platformOptions,
    isLoading,
    isSaving,
    error,
    success,
    detailsRow,
    hasPlatforms,
    platformLabelByValue,
    isCreateOpen,
    editingTargetId,
    deleteTargetId,
    form,
    formErrors,
    modalError,
    closeModals,
    openCreate,
    openEdit,
    openDetails,
    requestDelete,
    onFormChange,
    toggleAccountType,
    saveForm,
    confirmDelete,
  };
}
